import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiFilter, FiX } from "react-icons/fi";
import { getPurchaseOrders } from "../../Services/PurchaseOrderService";
import { CommonTable, Loader } from "../../Components/Common";
import {
  MESSAGES,
  PO_LIST_COLUMNS,
  PO_LIST_LABELS,
  PO_STATUS_STYLES,
  PO_RECEIVED_STATUS_STYLES,
  PO_BILLED_STATUS_STYLES,
  PO_RECEIVED_STATUS_OPTIONS,
  PO_BILLED_STATUS_OPTIONS,
  formatStatusLabel,
} from "./constants";
import { RESET_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { formatDateDDMMYYYY } from "../../Utils";
import { VIEW_PURCHASE_ORDER } from "../../routes";

const EMPTY_FILTERS = {
  expected_delivery_date: "",
  received_status: "",
  billed_status: "",
};

const POList = () => {
  const navigate = useNavigate();
  const [poList, setPoList] = useState([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [tempFilters, setTempFilters] = useState(EMPTY_FILTERS);

  const getPaginatedPOs = async (finalParams, isInitial = false) => {
    if (isInitial) {
      setInitialLoading(true);
    } else {
      setPaginationLoading(true);
    }

    try {
      const response = await getPurchaseOrders(finalParams);
      if (response.status === 200) {
        const newPOs = response.data.results || [];
        setPoList((prev) => (isInitial ? newPOs : [...prev, ...newPOs]));
        setPagination((prev) => ({
          ...prev,
          count: response.data.count,
          offset: prev.offset + prev.limit,
        }));
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.REQUEST_ERROR;
      toast.error(msg);
      if (isInitial) {
        setPoList([]);
      }
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      } else {
        setPaginationLoading(false);
      }
    }
  };

  const loadMorePOs = () => {
    if (pagination.offset >= pagination.count) return;
    getPaginatedPOs({
      offset: pagination.offset,
      limit: pagination.limit,
      ...filters,
    });
  };

  useEffect(() => {
    setPagination(RESET_PAGINATION);
    getPaginatedPOs({ offset: 0, limit: RESET_PAGINATION.limit, ...filters }, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const openFilters = () => {
    setTempFilters(filters);
    setShowFilters(true);
  };

  const applyFilters = () => {
    setFilters(tempFilters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    setTempFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setShowFilters(false);
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const renderFields = (key, row) => {
    switch (key) {
      case "expected_delivery_date":
        return formatDateDDMMYYYY(row.expected_delivery_date) || "-";
      case "status": {
        const style = PO_STATUS_STYLES[row.status] || "bg-gray-100 text-gray-700";
        return (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${style}`}
          >
            {formatStatusLabel(row.status)}
          </span>
        );
      }
      case "received_status": {
        const style =
          PO_RECEIVED_STATUS_STYLES[row.received_status] ||
          "bg-gray-100 text-gray-700";
        return (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${style}`}
          >
            {formatStatusLabel(row.received_status)}
          </span>
        );
      }
      case "billed_status": {
        const style =
          PO_BILLED_STATUS_STYLES[row.billed_status] || "bg-gray-100 text-gray-700";
        return (
          <span
            className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${style}`}
          >
            {formatStatusLabel(row.billed_status)}
          </span>
        );
      }
      default:
        return row[key] ?? "-";
    }
  };

  const handleRowClick = (row) => {
    navigate(VIEW_PURCHASE_ORDER.replace(":poId", row.id));
  };

  return (
    <div className="px-4 my-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xl md:text-2xl text-primary font-semibold">
          Purchase Order List
        </div>

        <button
          type="button"
          className="flex items-center gap-2 border rounded-md px-4 py-2 text-sm text-secondary hover:bg-gray-50"
          onClick={openFilters}
        >
          <FiFilter />
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-white text-xs">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div
        className="h-[72vh] overflow-x-auto overflow-y-auto md:border md:rounded-md"
        id="scrollablePoListDiv"
      >
        {initialLoading ? (
          <div className="flex justify-center items-center h-full py-10">
            <Loader size={4} />
          </div>
        ) : (
          <InfiniteScroll
            dataLength={poList.length}
            next={loadMorePOs}
            hasMore={pagination.offset < pagination.count}
            scrollableTarget="scrollablePoListDiv"
          >
            <CommonTable
              tableColumns={PO_LIST_COLUMNS}
              tableRows={poList}
              columnLabels={PO_LIST_LABELS}
              renderField={renderFields}
              rowClick={handleRowClick}
              customError={MESSAGES.NO_REQUESTS_AVAILABLE}
              className="text-center"
            />
            {paginationLoading && (
              <div className="flex justify-center py-4">
                <Loader size={3} />
              </div>
            )}
          </InfiniteScroll>
        )}
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm mx-4 rounded-2xl shadow-xl p-6 relative">
            <button
              type="button"
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              onClick={() => setShowFilters(false)}
            >
              <FiX size={18} />
            </button>
            <h3 className="text-lg font-semibold text-primary mb-4">Filters</h3>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={tempFilters.expected_delivery_date}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      expected_delivery_date: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:outline-none"
                />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Received Status
                </label>
                <select
                  value={tempFilters.received_status}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      received_status: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                >
                  <option value="">All</option>
                  {PO_RECEIVED_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {formatStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-1">
                  Billed Status
                </label>
                <select
                  value={tempFilters.billed_status}
                  onChange={(e) =>
                    setTempFilters((prev) => ({
                      ...prev,
                      billed_status: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-1 focus:ring-primary focus:outline-none"
                >
                  <option value="">All</option>
                  {PO_BILLED_STATUS_OPTIONS.map((status) => (
                    <option key={status} value={status}>
                      {formatStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-between items-center mt-2">
                <button
                  className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm"
                  onClick={clearFilters}
                >
                  Clear
                </button>
                <div className="flex gap-2">
                  <button
                    className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-sm text-gray-800"
                    onClick={() => setShowFilters(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="px-4 py-2 rounded-lg bg-primary hover:bg-secondary text-white text-sm"
                    onClick={applyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default POList;
